"use client";
/**
 * Manual-save gate for workspace ARRANGEMENT.
 *
 * The app normally persists every layout change to localStorage synchronously
 * (and mirrors it to the server). This gate turns that into an explicit Save:
 * while a workspace page is mounted, arrangement writes are buffered in memory
 * and marked "unsaved" instead of persisted; Save flushes the buffer to
 * localStorage (and the server); Discard drops it.
 *
 * Scope — ARRANGEMENT ONLY. Two kinds of arrangement write are gated:
 *   1. Explicit engine edits (grid pane add / remove / move / resize, split-tree
 *      structure) — routed here via {@link gatedSet} by the engines.
 *   2. Split-divider drag positions — react-resizable-panels persists these
 *      autonomously via its own `localStorage.setItem`; we intercept those with
 *      a patch on the arrangement-key prefix.
 * Per-pane CONTENT (chart symbol, drawings, indicators, scanner filters) is NOT
 * gated — it keeps auto-saving, so you never lose a trendline for want of a Save.
 * Nor is which panes are ROLLED UP or docked to the side: that list is kept at
 * once, like the maximized pane, and so is the layout write the library makes
 * for it ({@link expectRollUpWrite}).
 *
 * Discard is a reload: buffered edits were never written, so re-reading
 * localStorage restores the last saved arrangement exactly.
 */
import { safeLocalStorageSet } from "@/lib/safeLocalStorage";

const DIVIDER_PREFIX = "react-resizable-panels:";
/**
 * A chart's own split: its plot beside its Watchlist / Alerts / editor panel.
 *
 * That is chart chrome, like which of those panels is open (kept at once,
 * never through Save), not a page's arrangement, and it is NOT gated. It
 * cannot be: every chart in the app keeps this split under one key, and a
 * chart whose panel is closed stores it as [100, 0] while one whose panel is
 * open stores [72, 28]. So merely opening a page whose chart has its panel
 * the other way from the last chart shown wrote a "changed divider", and the
 * page opened on "Unsaved changes" with nothing touched: Charting did, every
 * time, after any Market Timing tab (seen in the owner's app, 2026-10-11; his
 * log holds 45 such discards). Its writes pass straight through.
 */
const CHART_CHROME_DIVIDER_PREFIX = "react-resizable-panels:mpp:advchart:side-panel:";

/**
 * Layout writes the library is about to make because a pane was rolled up or
 * opened: divider key → the instant the expectation lapses.
 *
 * Rolling a pane up in a row is not a divider drag, but the library cannot
 * tell them apart: it resizes the row's Panels and saves the row's layout
 * under the same key a drag does. The rolled-up list itself is kept at once.
 * Gating the layout write alone left the two apart: the page said "Unsaved
 * changes" for a roll-up, and after Discard (a reload) the list still said
 * rolled up, the saved layout said open, the row was resized again as the
 * page loaded, and that write was buffered too. So the page came back rolled
 * up and unsaved, and no Discard could clear it (until 2026-10-11).
 */
const rollUpWrites = new Map<string, number>();
/** The library saves 100 ms after the layout changes; a page of charts being
 *  resized can hold that timer back, so the allowance is generous. */
const ROLL_UP_WRITE_MS = 2000;

type Listener = () => void;
const listeners = new Set<Listener>();

/** Pending arrangement writes, not yet persisted (key → serialized value). */
const buffer = new Map<string, string>();
/**
 * Compensations for side effects that accompany a buffered arrangement edit but
 * cannot be buffered themselves. Dragging a pane onto another moves that pane's
 * saved state to its new slot, which is a RENAME (delete + write) — the buffer
 * only holds writes, and pane content is deliberately ungated anyway. Without
 * this, Discard would restore the old arrangement with the two panes' contents
 * left swapped. Run newest-first on Discard; dropped on Save.
 */
const undos: Array<() => void> = [];
let dirty = false;
let activeCount = 0; // number of mounted gated pages
let dividerArmed = false; // true a frame after mount, so the library's initial autosave passes through
let flushing = false; // true while Save writes the buffer out (bypass the gate)
let patched = false;
let prevSet: ((k: string, v: string) => void) | null = null;
let prevGet: ((k: string) => string | null) | null = null;

function emit(): void {
  for (const l of listeners) l();
}

/** Subscribe to dirty-state changes (for useSyncExternalStore). */
export function subscribeSaveGate(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function isSaveGateDirty(): boolean {
  return dirty;
}

/** The effective current value of a key: buffered edit if any, else real storage. */
function effective(key: string): string | null {
  if (buffer.has(key)) return buffer.get(key)!;
  return prevGet ? prevGet(key) : localStorage.getItem(key);
}

/** Buffer an arrangement write. No-op writes (the library re-saving the same
 *  value on mount) are ignored so the page never loads "dirty". */
function bufferWrite(key: string, value: string): void {
  if (effective(key) === value) return;
  buffer.set(key, value);
  if (!dirty) {
    dirty = true;
    emit();
  }
}

function ensurePatched(): void {
  if (patched || typeof window === "undefined") return;
  patched = true;
  const ls = window.localStorage;
  // Capture the CURRENT setItem — installed after PrefsSyncGate wires up its
  // server-mirror patch, so prevSet mirrors synced writes on flush.
  prevSet = ls.setItem.bind(ls);
  prevGet = ls.getItem.bind(ls);
  ls.setItem = (k: string, v: string) => {
    const key = String(k);
    const val = String(v);
    // The one write a roll-up announced, whenever it lands: taken here so an
    // allowance never outlives the write it was for.
    const rolledUp = takeRollUpWrite(key);
    // Autonomous divider writes: gate once armed (past the initial mount).
    if (!flushing && activeCount > 0 && dividerArmed && key.startsWith(DIVIDER_PREFIX) && !key.startsWith(CHART_CHROME_DIVIDER_PREFIX)) {
      // A divider with no baseline yet is the library establishing its initial
      // size — persist it as the baseline, don't mark dirty. Only a later change
      // to a known divider is a real edit.
      if (effective(key) === null) {
        prevSet!(key, val);
        return;
      }
      // A pane rolled up or opened: kept at once, with the list that says so.
      // Unless this row already holds an unsaved drag: the write carries the
      // whole row, so it joins that edit and waits for Save with it.
      if (rolledUp && !buffer.has(key)) {
        prevSet!(key, val);
        return;
      }
      bufferWrite(key, val);
      return;
    }
    prevSet!(key, val);
    // Immediate writes (e.g. CustomPageWorkspace `savePages`, or ungated
    // engines) must invalidate any stale buffer entry for the same key.
    // Otherwise a later Save on another page can flush an older arrangement
    // over the newer LS value — Development Testing layouts looked like they
    // "didn't persist" after visiting Dashboard / Daily Brief and clicking Save.
    if (!flushing && buffer.has(key)) {
      buffer.delete(key);
      if (buffer.size === 0 && dirty) {
        dirty = false;
        emit();
      }
    }
  };
  ls.getItem = (k: string) => {
    const key = String(k);
    // While a gated page is active, buffered edits shadow real storage so a
    // remount (e.g. a split re-creating PanelGroups) sees the unsaved value.
    if (activeCount > 0 && buffer.has(key)) return buffer.get(key)!;
    return prevGet!(key);
  };
}

/** Whether `key`'s next write was announced by a roll-up, consuming the allowance. */
function takeRollUpWrite(key: string): boolean {
  const until = rollUpWrites.get(key);
  if (until === undefined) return false;
  rollUpWrites.delete(key);
  return Date.now() <= until;
}

/**
 * A pane in the row saved under `autoSaveId` is about to be rolled up or
 * opened (by a press, or as a page loads with a rolled-up list its saved
 * layout does not show). The row's next layout write, within two seconds, is
 * that and not a drag: it is kept at once instead of waiting for Save.
 *
 * Recorded whether or not a gated page is active yet: as a page mounts, a
 * pane's effect runs before its page's, so the announcement for a row that
 * is resized on load arrives before {@link activateSaveGate}. Unused, it
 * lapses by the clock.
 */
export function expectRollUpWrite(autoSaveId: string): void {
  if (typeof window === "undefined") return;
  rollUpWrites.set(DIVIDER_PREFIX + autoSaveId, Date.now() + ROLL_UP_WRITE_MS);
}

/**
 * Mount hook — a gated workspace page calls this on mount and the returned
 * disposer on unmount. Buffered edits deliberately survive in-app navigation
 * (only a full reload clears them), so unsaved work isn't lost when you tab away.
 */
export function activateSaveGate(): () => void {
  if (typeof window === "undefined") return () => {};
  ensurePatched();
  activeCount++;
  if (activeCount === 1) {
    // Arm divider gating one frame after the first gated page mounts, so the
    // library's initial (equal-value) autosave isn't mistaken for an edit.
    requestAnimationFrame(() => {
      dividerArmed = true;
    });
  }
  return () => {
    activeCount = Math.max(0, activeCount - 1);
    if (activeCount === 0) dividerArmed = false;
  };
}

/**
 * Route an explicit engine arrangement edit through the gate. When no gated page
 * is active it writes straight through (immediate), preserving behavior for any
 * caller outside a workspace.
 */
export function gatedSet(key: string, value: string): void {
  if (typeof window === "undefined") return;
  if (activeCount > 0) {
    ensurePatched();
    bufferWrite(key, value);
  } else {
    safeLocalStorageSet(key, value);
  }
}

/**
 * Register an undo for an ungatable side effect of the current arrangement edit
 * (see {@link undos}). No-op when no gated page is active — there the edit
 * persisted for real, so there is nothing to take back.
 */
export function registerGateUndo(undo: () => void): void {
  if (typeof window === "undefined" || activeCount === 0) return;
  undos.push(undo);
}

/** Save — flush every buffered arrangement write to localStorage (and, for
 *  synced `mpp:*` keys, to the server) and clear the dirty flag. */
export function saveGateCommit(): void {
  if (typeof window === "undefined") return;
  undos.length = 0; // the edits are permanent now — nothing left to take back
  if (buffer.size === 0) {
    if (dirty) {
      dirty = false;
      emit();
    }
    return;
  }
  flushing = true;
  try {
    for (const [k, v] of buffer) {
      if (k.startsWith("mpp:")) safeLocalStorageSet(k, v); // quota-safe; mirrors to the server only if isSyncedKey (per-machine layout keys are not)
      else prevSet!(k, v); // divider keys (react-resizable-panels:*): per-machine, not synced — write straight through the lower patches (namespace → real)
    }
  } finally {
    flushing = false;
  }
  buffer.clear();
  dirty = false;
  emit();
}

/** Discard — drop every buffered edit. The caller must reload so in-memory
 *  React / library state re-reads the last saved arrangement. */
export function saveGateDiscard(): void {
  // Newest first, so a pane dragged twice lands back where it started.
  for (let i = undos.length - 1; i >= 0; i--) {
    try {
      undos[i]!();
    } catch {
      /* a compensation that can't run must not block the discard */
    }
  }
  undos.length = 0;
  buffer.clear();
  if (dirty) {
    dirty = false;
    emit();
  }
}
